import React from 'react';
import { Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { renderField, renderSelectedDataElement, renderListView } from './fieldRenderers';
import { renderConditionsTable, renderLayoutTable, renderExternalFilters, renderSplitPane, renderBooleanFieldsTable, renderAdjustPaneSize, renderBlocksTable, renderChartMetadataTable } from './tableRenderers';
import { renderNestedObject } from './nestedObjectRenderer';
import { renderSteps, renderHeaderTableConfig, renderTabsConfig, renderSelectedTopLevelDataElement, renderItemsObject, renderCompareLineItemsAndPOInfo } from './specialRenderers';
import { FieldRouter } from './components/FieldRouter';

export const renderChartData = (chartData, chart = null, classes) => {
	if (!chartData || typeof chartData !== 'object') {
		return <Typography color="textSecondary">(No chart data)</Typography>;
	}

	const booleanFieldsList = [
		'considerErrorsAsWarnings',
		'showHeaderTable',
		'showPdfHighlights',
		'updateInputOnHighlight',
		'searchablePDF',
		'showSearchablePdfDefault',
		'lockSupport',
		'expandLineItems',
		'hidePDF',
		'showTimer',
		'showLineItemInFullScreen',
	];

	return (
		<Box>
			{/* Chart metadata table (name, _id, chartUniqueId, chartType, selectedDataModel, selectedTopLevelDataElement) */}
			{renderChartMetadataTable(chartData, chart, classes)}

			{/* 3. listView section (if config available) */}
			{chartData.listView && (
				<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
						listView:
					</Typography>
					{renderListView(chartData.listView, classes, renderSelectedDataElement)}
				</Box>
			)}

			{/* 5. selectedDataElement (data elements) */}
			{chartData.selectedDataElement && (
				<Box mb={2} display="flex" alignItems="center" flexWrap="wrap">
					<Typography component="span" className={classes.fieldLabel} style={{ minWidth: 180 }}>
						grid view's selectedDataElement:
					</Typography>
					{renderSelectedDataElement(chartData.selectedDataElement, classes)}
				</Box>
			)}

			{/* Steps section */}
			{chartData.steps && Array.isArray(chartData.steps) && chartData.steps.length > 0 && (
				<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
						steps:
					</Typography>
					{renderSteps(chartData.steps, classes)}
					{/* Visual separator to indicate steps section end */}
					<Box
						mt={3}
						mb={1}
						style={{
							borderBottom: '3px solid #1976d2',
							position: 'relative',
							paddingBottom: 8
						}}
					>
						<Typography
							variant="caption"
							style={{
								color: '#1976d2',
								fontWeight: 600,
								fontSize: '0.75rem',
								letterSpacing: '0.5px',
								textTransform: 'uppercase'
							}}
						>
							━━ End of Steps ━━
						</Typography>
					</Box>
				</Box>
			)}

			{/* headerTableConfig section */}
			{chartData.headerTableConfig && typeof chartData.headerTableConfig === 'object' && (
				<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
						headerTableConfig:
					</Typography>
					{renderHeaderTableConfig(chartData.headerTableConfig, classes)}
				</Box>
			)}

			{/* 6. conditions section */}
			{chartData.conditions && Array.isArray(chartData.conditions) && chartData.conditions.length > 0 && (
				<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
						conditions:
					</Typography>
					{renderConditionsTable(chartData.conditions)}
				</Box>
			)}

			{/* 7. Download section */}
			{chartData.download !== undefined && chartData.download !== null && (
				<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
						download:
					</Typography>
					{typeof chartData.download === 'boolean' ? (
						<Box display="flex" alignItems="center">
							{chartData.download === true ? (
								<>
									<CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
									<Typography component="span">true</Typography>
								</>
							) : (
								<>
									<CancelIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
									<Typography component="span">false</Typography>
								</>
							)}
						</Box>
					) : typeof chartData.download === 'object' && !Array.isArray(chartData.download) ? (
						Object.entries(chartData.download).map(([key, value]) => (
							<Box key={key} mb={2}>
								{Array.isArray(value) ? (
									<Box>
										<Typography component="span" className={classes.fieldLabel} style={{ minWidth: 180 }}>
											{key}:
										</Typography>
										{value.length === 0 ? (
											<Typography color="textSecondary">(Empty)</Typography>
										) : key === 'selectedDataElement' ? (
											renderSelectedDataElement(value, classes)
										) : (
											<Typography>
												{value
													.map((item) => {
														if (typeof item === 'object' && item !== null && item.value) {
															return item.value;
														}
														return String(item);
													})
													.filter((v) => v !== undefined && v !== null)
													.join(', ')}
											</Typography>
										)}
									</Box>
								) : typeof value === 'object' && value !== null ? (
									<Box>
										<Typography component="span" className={classes.fieldLabel} style={{ minWidth: 180 }}>
											{key}:
										</Typography>
										<Chip
											label={`Object (${Object.keys(value).length} keys)`}
											className={classes.chip}
											size="small"
										/>
									</Box>
								) : (
									renderField(key, value, classes)
								)}
							</Box>
						))
					) : null}
				</Box>
			)}

			{/* 8. layout section */}
			{chartData.layout && (
				<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
						layout:
					</Typography>
					{renderLayoutTable(chartData.layout)}
				</Box>
			)}

			{/* 9. externalFilters section */}
			{chartData.externalFilters && Array.isArray(chartData.externalFilters) && chartData.externalFilters.length > 0 && (
				<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
						externalFilters:
					</Typography>
					{renderExternalFilters(chartData.externalFilters)}
				</Box>
			)}

			{/* 10. splitPane section */}
			{chartData.splitPane && (
				<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
						splitPane:
					</Typography>
					{renderSplitPane(chartData.splitPane)}
				</Box>
			)}

			{/* 11. compareLineItemsAndPOInfo section */}
			{chartData.compareLineItemsAndPOInfo && typeof chartData.compareLineItemsAndPOInfo === 'object' && !Array.isArray(chartData.compareLineItemsAndPOInfo) && (
				<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
					<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
						compareLineItemsAndPOInfo:
					</Typography>
					{renderCompareLineItemsAndPOInfo(chartData.compareLineItemsAndPOInfo, classes)}
				</Box>
			)}

			{/* 12. Component specific settings table section */}
			{(() => {
				const booleanTable = renderBooleanFieldsTable(chartData);
				const adjustPaneSizeTable = chartData.adjustPaneSize ? renderAdjustPaneSize(chartData.adjustPaneSize) : null;

				if (!booleanTable && !adjustPaneSizeTable) {
					return null;
				}

				return (
					<Box mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
						<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
							Component specific Settings:
						</Typography>
						{booleanTable}
						{adjustPaneSizeTable}
					</Box>
				);
			})()}

			{/* Render other fields */}
			<Box mt={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
				{(() => {
					// Separate simple fields from complex ones
					const simpleFields = [];
					const complexFields = [];

					Object.entries(chartData).forEach(([key, value]) => {
						// Skip already rendered fields
						const skipFields = [
							'name',
							'selectedDataModel',
							'selectedDataElement',
							'selectedTopLevelDataElement',
							'listView',
							'conditions',
							'download',
							'layout',
							'externalFilters',
							'externalFilter',
							'splitPane',
							'adjustPaneSize',
							'steps',
							'headerTableConfig',
							'tabsConfig',
							'lineItems',
							'poLineItems',
							'summaryItems',
							'dataUrl',
							'fetchType',
							'annotationRedirectUrl',
							'externalFilter',
							'compareLineItemsAndPOInfo',
							...booleanFieldsList,
							'splitPDF',
							'splitpdf',
						];
						if (skipFields.includes(key)) return;

						const isComplex = typeof value === 'object' && value !== null &&
							key !== 'selectedDataElement' &&
							key !== 'selectedTopLevelDataElement' &&
							key !== 'splitPane' &&
							key !== 'autoRefresh' &&
							key !== 'headerTableConfig' &&
							key !== 'headerTableConditionConfig' &&
							key !== 'tabsConfig' &&
							key !== 'externalFilters' &&
							key !== 'externalFilter' &&
							key !== 'lineItems' &&
							key !== 'poLineItems' &&
							key !== 'summaryItems' &&
							key !== 'steps' &&
							key !== 'compareLineItemsAndPOInfo' &&
							!booleanFieldsList.includes(key);

						if (isComplex || Array.isArray(value)) {
							complexFields.push({ key, value });
						} else {
							simpleFields.push({ key, value });
						}
					});

					// Separate autoRefresh from simpleFields
					const autoRefreshData = simpleFields.find(({ key }) => key === 'autoRefresh');
					const fieldsWithoutAutoRefresh = simpleFields.filter(({ key }) => key !== 'autoRefresh');

					// Split fields into two halves for side-by-side display
					const midpoint = Math.ceil(fieldsWithoutAutoRefresh.length / 2);
					const firstHalf = fieldsWithoutAutoRefresh.slice(0, midpoint);
					const secondHalf = fieldsWithoutAutoRefresh.slice(midpoint);

					// Helper function to render a table
					const renderSimpleFieldsTable = (fields) => (
						<TableContainer component={Paper} style={{ marginBottom: 16 }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell style={{ fontWeight: 600, width: '30%' }}>Property</TableCell>
										<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{fields.map(({ key, value }) => (
										<TableRow key={key}>
											<TableCell>{key}:</TableCell>
											<TableCell>
												{typeof value === 'boolean' ? (
													value === true ? (
														<Box display="flex" alignItems="center">
															<CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
															<Typography component="span">{String(value)}</Typography>
														</Box>
													) : (
														<Box display="flex" alignItems="center">
															<CancelIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
															<Typography component="span">{String(value)}</Typography>
														</Box>
													)
												) : (
													<Typography component="span">{String(value !== undefined && value !== null ? value : '-')}</Typography>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					);

					return (
						<Box>
							{fieldsWithoutAutoRefresh.length > 0 && (
								<Grid container spacing={2} style={{ marginBottom: 16 }}>
									<Grid item xs={12} sm={6}>
										{firstHalf.length > 0 && renderSimpleFieldsTable(firstHalf)}
									</Grid>
									<Grid item xs={12} sm={6}>
										{secondHalf.length > 0 && renderSimpleFieldsTable(secondHalf)}
									</Grid>
								</Grid>
							)}
							{autoRefreshData && typeof autoRefreshData.value === 'object' && autoRefreshData.value !== null && !Array.isArray(autoRefreshData.value) && (
								<Box mb={2}>
									<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
										{autoRefreshData.key}:
									</Typography>
									{renderItemsObject(autoRefreshData.value, classes)}
								</Box>
							)}
							{complexFields.map(({ key, value }) => {
								// Handle arrays - display without accordion
								if (Array.isArray(value)) {
									return (
										<Box key={key} mb={2}>
											<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
												{key}:
											</Typography>
											{value.length === 0 ? (
												<Typography color="textSecondary" style={{ marginLeft: 16 }}>(Empty array)</Typography>
											) : (
												<Box style={{ marginLeft: 16 }}>
													{value.map((item, index) => (
														<Box key={index} mb={1}>
															{typeof item === 'object' && item !== null ? (
																renderNestedObject(item, 0, [], classes)
															) : (
																<Typography>{String(item)}</Typography>
															)}
														</Box>
													))}
												</Box>
											)}
										</Box>
									);
								}

								// Handle compareLineItemsAndPOInfo as table FIRST (before other handlers)
								if (key === 'compareLineItemsAndPOInfo' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
									return (
										<Box key={key} mt={2} mb={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
											<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
												{key}:
											</Typography>
											{renderItemsObject(value, classes)}
										</Box>
									);
								}

								// Handle autoRefresh as table
								if (key === 'autoRefresh' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
									return (
										<Box key={key} mb={2}>
											<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>
												{key}:
											</Typography>
											{renderItemsObject(value, classes)}
										</Box>
									);
								}

								// Handle externalFilter - arrays as chips, objects as tables
								if (key === 'externalFilter') {
									if (Array.isArray(value)) {
										return (
											<Box key={key} mb={2}>
												<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
													{key}:
												</Typography>
												{renderExternalFilters(value)}
											</Box>
										);
									} else if (typeof value === 'object' && value !== null) {
										return (
											<Box key={key} mb={2}>
												<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
													{key}:
												</Typography>
												{renderItemsObject(value, classes)}
											</Box>
										);
									}
								}

								// Handle headerTableConditionConfig as table without accordion
								if (key === 'headerTableConditionConfig' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
									return (
										<Box key={key} mb={2}>
											<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
												{key}:
											</Typography>
											{renderItemsObject(value, classes)}
										</Box>
									);
								}

								// Handle pagination as table without accordion
								if (key === 'pagination' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
									return (
										<Box key={key} mb={2}>
											<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
												{key}:
											</Typography>
											{renderItemsObject(value, classes)}
										</Box>
									);
								}

								if (typeof value === 'object' && value !== null && key !== 'selectedDataElement' && key !== 'selectedTopLevelDataElement' && key !== 'splitPane' && key !== 'autoRefresh' && key !== 'headerTableConfig' && key !== 'headerTableConditionConfig' && key !== 'tabsConfig' && key !== 'externalFilters' && key !== 'externalFilter' && key !== 'lineItems' && key !== 'poLineItems' && key !== 'summaryItems' && key !== 'steps' && key !== 'compareLineItemsAndPOInfo' && key !== 'pagination' && !booleanFieldsList.includes(key)) {
									return (
										<Box key={key} mb={1}>
											<Accordion className={classes.nestedAccordion}>
												<AccordionSummary expandIcon={<ExpandMoreIcon />}>
													<Typography>
														<strong>{key}</strong>:{' '}
														{Array.isArray(value)
															? `Array (${value.length})`
															: `Object (${Object.keys(value).length} keys)`}
													</Typography>
												</AccordionSummary>
												<AccordionDetails>
													{key === 'download' && value.selectedDataElement ? (
														<Box>
															<Box mb={2}>
																<Typography component="span" className={classes.fieldLabel}>
																	selectedDataElement:
																</Typography>
																{renderSelectedDataElement(value.selectedDataElement, classes)}
															</Box>
															{renderNestedObject(value, 1, ['selectedDataElement'], classes)}
														</Box>
													) : (() => {
														const routed = <FieldRouter fieldKey={key} value={value} classes={classes} showLabel={false} />;
														return routed || renderNestedObject(value, 1, [], classes);
													})()}
												</AccordionDetails>
											</Accordion>
										</Box>
									);
								}

								// Use FieldRouter directly to avoid duplicate labels
								const routed = <FieldRouter fieldKey={key} value={value} classes={classes} showLabel={true} />;
								if (routed) {
									return (
										<Box key={key} mb={1}>
											{routed}
										</Box>
									);
								}

								// Fallback for unhandled fields
								return (
									<Box key={key} mb={1}>
										{renderField(key, value, classes)}
									</Box>
								);
							})}
						</Box>
					);
				})()}
			</Box>
		</Box>
	);
};

